import type { ChainEnvelope, ChainStatus } from '@/types/searchItemsTypes'

export type ChainOutcome = 'success' | 'failed' | 'pending'

const SUCCESS_STATUSES = new Set(['success', 'completed', 'ok'])
const FAILED_STATUSES = new Set(['failed', 'error'])

/**
 * Anything the backend does not explicitly call terminal is treated as still
 * running — new in-progress state names must not read as failures here.
 */
export function classifyChainStatus(status: ChainStatus | undefined | null): ChainOutcome {
  const normalized = String(status ?? '').toLowerCase()
  if (SUCCESS_STATUSES.has(normalized)) return 'success'
  if (FAILED_STATUSES.has(normalized)) return 'failed'
  return 'pending'
}

/** A chain the backend gave up on — surfaced to the user as an error state. */
export class ChainFailedError extends Error {
  readonly chainId: string | null
  // Declared explicitly: Error.cause is not in this project's TS lib target.
  readonly cause: Error

  constructor(message: string, chainId: string | null = null) {
    super(message)
    this.name = 'ChainFailedError'
    this.chainId = chainId
    // The query layer normalizes thrown errors into a plain object, keeping only
    // `message` and `cause` — so the instance is carried on `cause` to survive.
    this.cause = this
  }
}

/** The chain never reached a terminal state inside the overall ceiling. */
export class ChainTimeoutError extends Error {
  readonly chainId: string | null
  readonly cause: Error

  constructor(chainId: string | null = null) {
    super('Chain did not reach a terminal state in time')
    this.name = 'ChainTimeoutError'
    this.chainId = chainId
    this.cause = this
  }
}

/** The chain was superseded by a newer search — not a user-facing failure. */
export class ChainAbortedError extends Error {
  readonly cause: Error

  constructor() {
    super('Chain resolution aborted')
    this.name = 'ChainAbortedError'
    this.cause = this
  }
}

/**
 * Matches both a raw throw and the normalized `{ message, cause }` shape the
 * query layer rethrows, so callers can branch on the failure kind either way.
 */
function matchesChainError<T>(error: unknown, type: new (...args: never[]) => T): boolean {
  if (error instanceof type) return true
  const cause = (error as { cause?: unknown } | null)?.cause
  return cause instanceof type
}

export const isChainTimeoutError = (error: unknown): boolean =>
  matchesChainError(error, ChainTimeoutError)

export const isChainFailedError = (error: unknown): boolean =>
  matchesChainError(error, ChainFailedError)

/** Covers our own abort plus axios/DOM cancellations of the underlying request. */
export const isAbortError = (error: unknown): boolean => {
  if (matchesChainError(error, ChainAbortedError)) return true
  const name = (error as { name?: string } | null)?.name
  return name === 'AbortError' || name === 'CanceledError'
}

export interface UnwrappedChain<TResult> {
  outcome: ChainOutcome
  status: ChainStatus
  chainId: string | null
  isBackground: boolean
  result: TResult | null
  error: string | null
}

/**
 * Payloads that predate the envelope carry the result fields at the top level.
 * Detected by a positive marker of the old shape rather than by "not obviously
 * an envelope" — otherwise a bare `{status: "PENDING"}` would be mistaken for a
 * successful result and silently render as an empty list.
 */
function isLegacyFlatResult(payload: unknown): boolean {
  return typeof payload === 'object' && payload !== null && 'search_results' in payload
}

function isEnvelope(payload: unknown): payload is ChainEnvelope<unknown> {
  return typeof payload === 'object' && payload !== null && 'status' in payload
}

/**
 * Normalizes a raw shopping-list payload into the pieces callers care about.
 * `generated_image_url` lives on the envelope but consumers have always read it
 * off the result, so it is folded back in here rather than at each call site.
 */
export function unwrapChainEnvelope<TResult extends object>(
  payload: unknown
): UnwrappedChain<TResult> {
  // A flat payload that also carries `status` is still the legacy shape.
  if (!isEnvelope(payload) || isLegacyFlatResult(payload)) {
    return {
      outcome: isLegacyFlatResult(payload) ? 'success' : 'pending',
      status: 'success',
      chainId: null,
      isBackground: false,
      result: (payload as TResult) ?? null,
      error: null
    }
  }

  const envelope = payload as ChainEnvelope<TResult>
  const chainId = envelope.chain_id ?? null
  const result = envelope.result ?? null

  return {
    outcome: classifyChainStatus(envelope.status),
    status: envelope.status,
    chainId,
    isBackground: Boolean(envelope.is_background),
    result:
      result === null
        ? null
        : ({
            ...result,
            chain_id: chainId,
            generated_image_url:
              envelope.generated_image_url ??
              (result as { generated_image_url?: string | null }).generated_image_url ??
              null
          } as TResult),
    error: envelope.error ?? null
  }
}

/**
 * The `/shopping-list` endpoint returns two shapes on one contract: text search
 * gives a flat product list, image search groups candidates by detected object
 * (`{ object_index, enriched_results }`). The UI renders a flat
 * `ProductCustomCardItem[]`, so collapse any groups into one list. A payload that
 * is already flat has no `enriched_results` and is returned untouched — so this
 * is safe to apply to EVERY shopping-list result (text stays exactly as-is).
 */
export function flattenSearchGroups<T extends { search_results?: unknown }>(result: T): T {
  const raw = result?.search_results
  if (!Array.isArray(raw)) {
    return result
  }
  const grouped = raw.some(isSearchGroup)
  const cards = grouped
    ? raw.flatMap((entry) => (isSearchGroup(entry) ? (entry.enriched_results ?? []) : [entry]))
    : raw

  let changed = grouped
  const normalized = cards.map((card) => {
    const withImages = deriveImageUrls(card)
    if (withImages !== card) changed = true
    return withImages
  })

  return changed ? { ...result, search_results: normalized } : result
}

function isSearchGroup(value: unknown): value is { enriched_results?: unknown[] } {
  return typeof value === 'object' && value !== null && 'enriched_results' in value
}

/**
 * TEMPORARY: derive image_urls from images while the backend image_urls→images
 * migration completes (M-IDX-1); becomes no-op once search returns flat
 * product-level image_urls.
 *
 * A card that already carries a non-empty flat `image_urls` (text search today,
 * and every result after M-IDX-1) is returned untouched. Only a card left with
 * an `images` array and no `image_urls` is mapped — main picture first — so the
 * `CustomCardBlock` `<img>` has a src.
 */
function deriveImageUrls(card: unknown): unknown {
  if (typeof card !== 'object' || card === null) {
    return card
  }
  const c = card as { image_urls?: unknown; images?: unknown }
  if (Array.isArray(c.image_urls) && c.image_urls.length > 0) {
    return card
  }
  if (!Array.isArray(c.images) || c.images.length === 0) {
    return card
  }
  const image_urls = [...c.images]
    .sort((a, b) => Number(isMainPicture(b)) - Number(isMainPicture(a)))
    .map((img) => (typeof img === 'object' && img !== null ? (img as { url?: string }).url : undefined))
    .filter((url): url is string => typeof url === 'string' && url.length > 0)
  return { ...c, image_urls }
}

function isMainPicture(img: unknown): boolean {
  return typeof img === 'object' && img !== null && (img as { main_picture?: boolean }).main_picture === true
}
